import { useEffect, useState } from "react";
import { copy } from "../copy";
import { formatCountdown } from "../format";
import { contestHref } from "../feed";
import type { BeatPlayer } from "../audio";
import BeatBars from "../components/BeatBars";
import Icon from "../components/Icon";
import type { WebContest } from "./data";

const HOUR_MS = 3_600_000;

const chip =
  "rounded-lg border border-ground-edge bg-ground/85 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.14em] backdrop-blur-sm";

// The top-right corner of the cover: time left while a contest is open, its
// state once it is not. A deadline in the past never shows negative time.
function Status({ contest }: { contest: WebContest }) {
  const end = new Date(contest.ends_at).getTime();
  const [now, setNow] = useState(() => Date.now());
  const ticking = contest.state === "open" && Number.isFinite(end) && end > now;

  useEffect(() => {
    if (!ticking) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [ticking]);

  if (contest.state === "complete") {
    return <span className={`${chip} text-gold`}>{copy.contests.complete}</span>;
  }
  if (contest.state === "judging") {
    return <span className={`${chip} text-chrome-dim`}>{copy.contests.judging}</span>;
  }
  const left = Number.isFinite(end) ? formatCountdown(end - now) : null;
  if (left === null) return <span className={`${chip} text-chrome-dim`}>{copy.contests.closed}</span>;
  const urgent = end - now < HOUR_MS;
  return (
    <span data-testid="card-countdown" className={`bf-tabular ${chip} ${urgent ? "text-alert" : "text-chrome"}`}>
      <span className="sr-only">{copy.contests.closesIn(left)}</span>
      <span aria-hidden>{left}</span>
    </span>
  );
}

// The prize line leads with the pool when a market exists, in money green so
// it reads first; the fee is on the contest page. Any other kind shows the
// server's display string or label as sent.
function Prize({ contest }: { contest: WebContest }) {
  const { prize } = contest;
  if (prize.kind === "pool" && prize.display) {
    return (
      <div
        data-testid="card-prize"
        className="flex items-center justify-between rounded-lg border border-money/40 bg-money/10 px-3 py-2.5"
      >
        <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-money">{copy.contests.pool}</span>
        <span className="bf-tabular font-headline text-2xl leading-none text-money">{prize.display}</span>
      </div>
    );
  }
  if (prize.kind === "cash" && prize.display) {
    return (
      <div data-testid="card-prize" className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-[0.14em] text-chrome-dim">{copy.contests.cashPrize}</span>
        <span className="bf-tabular font-headline text-lg leading-none text-gold">{prize.display}</span>
      </div>
    );
  }
  if (prize.kind === "prize" && prize.label) {
    return (
      <div data-testid="card-prize" className="flex items-center justify-between gap-3">
        <span className="text-[11px] uppercase tracking-[0.14em] text-chrome-dim">{copy.contests.prizeLabel}</span>
        <span className="truncate text-sm font-bold text-chrome">{prize.label}</span>
      </div>
    );
  }
  return null;
}

function admissionText(contest: WebContest): string {
  const { admission } = contest;
  if (admission.kind === "fee" && admission.display) return copy.contests.entry(admission.display);
  if (admission.kind === "pay_as_you_go") return copy.contests.payAsYouGo;
  return copy.contests.free;
}

function Preview({ contest, player }: { contest: WebContest; player: BeatPlayer }) {
  const url = contest.preview_url;
  if (!url) return null;
  const key = `contest:${contest.slug}`;
  const playing = player.playingKey === key;
  return (
    <div className="flex h-10 items-center gap-3 rounded-lg border border-ground-edge bg-ground px-2">
      <button
        type="button"
        data-testid="card-play"
        aria-pressed={playing}
        aria-label={playing ? copy.contests.pause(contest.title) : copy.contests.play(contest.title)}
        onClick={() => player.toggle(key, url)}
        className="bf-focusable flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-money text-ground transition-colors hover:bg-money-deep"
      >
        {playing ? (
          <svg aria-hidden viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-current">
            <path d="M6 4h4v16H6zM14 4h4v16h-4z" />
          </svg>
        ) : (
          <svg aria-hidden viewBox="0 0 24 24" className="ml-0.5 h-3.5 w-3.5 fill-current">
            <path d="M7 4.5v15l13-7.5z" />
          </svg>
        )}
      </button>
      <div className="h-6 min-w-0 flex-1">
        <BeatBars active={playing} read={player.readLevels} />
      </div>
    </div>
  );
}

// A contest on the board. The title and cover are plain anchors: Go serves
// the contest page, so a router link would render the app's 404.
export default function ContestCard({
  contest,
  player,
  reducedMotion,
}: {
  contest: WebContest;
  player: BeatPlayer;
  reducedMotion: boolean;
}) {
  const href = contestHref(contest.slug);
  const seatsOpen = Math.max(0, contest.seats_total - contest.seats_taken);
  const zoom = reducedMotion ? "" : "transition-transform duration-500 group-hover:scale-[1.04]";

  return (
    <article
      data-testid="contest-listing"
      className="group flex min-w-0 flex-col overflow-hidden rounded-2xl border border-ground-edge bg-ground-raised transition-colors hover:border-chrome-dim/40"
    >
      <a
        href={href}
        aria-label={copy.contests.open(contest.title)}
        className="bf-focusable relative block aspect-[4/3] w-full overflow-hidden"
      >
        {contest.cover_url ? (
          <img
            src={contest.cover_url}
            alt={copy.contests.cover(contest.title)}
            loading="lazy"
            className={`h-full w-full object-cover object-top ${zoom}`}
          />
        ) : (
          <div
            data-testid="cover-fallback"
            className="flex h-full w-full items-center justify-center bg-[radial-gradient(circle_at_30%_20%,rgba(247,201,72,0.18),transparent_55%),radial-gradient(circle_at_80%_90%,rgba(43,217,107,0.14),transparent_50%)] bg-ground"
          >
            <Icon name="disc" className="h-14 w-14 text-chrome-dim/60" />
          </div>
        )}
        <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-ground via-ground/20 to-transparent" />
        <div className="absolute right-2 top-2">
          <Status contest={contest} />
        </div>
        {contest.genre && (
          <span className="absolute bottom-2 left-2 rounded-full border border-ground-edge bg-ground/85 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-chrome-dim backdrop-blur-sm">
            {contest.genre}
          </span>
        )}
      </a>

      <div className="flex flex-1 flex-col p-4">
        {(contest.viewer?.sponsor || contest.viewer?.entered || contest.is_seeded || contest.format === "versus") && (
          <div className="mb-2 flex flex-wrap items-center gap-1.5">
            {contest.viewer?.sponsor && (
              <span className="rounded-full bg-gold/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em] text-gold">
                {copy.contests.sponsor}
              </span>
            )}
            {contest.viewer?.entered && (
              <span className="rounded-full bg-money/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em] text-money">
                {copy.contests.entered}
              </span>
            )}
            {contest.format === "versus" && (
              <span className="rounded-full border border-ground-edge px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em] text-chrome">
                {copy.contests.versus}
              </span>
            )}
            {contest.is_seeded && (
              <span className="rounded-full border border-gold/40 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em] text-gold">
                {copy.contests.seeded}
              </span>
            )}
          </div>
        )}

        <a
          href={href}
          data-testid="contest-title"
          className="bf-focusable block break-words font-headline text-xl uppercase leading-tight tracking-[-0.01em] text-chrome transition-colors hover:text-gold"
        >
          {contest.title}
        </a>

        {(contest.challenge || contest.host_username) && (
          <p className="mt-2 flex min-w-0 items-center gap-1.5 text-[12px] text-chrome-dim">
            <Icon name="music" className="h-3.5 w-3.5 text-gold" />
            <span className="truncate">
              {[contest.challenge, contest.host_username && `@${contest.host_username}`].filter(Boolean).join(" · ")}
            </span>
          </p>
        )}

        {contest.state === "complete" && contest.winner_username && (
          <p data-testid="card-winner" className="mt-2 flex items-center gap-1.5 text-[12px] font-bold text-gold">
            <Icon name="trophy" className="h-3.5 w-3.5" />
            {copy.contests.wonBy(contest.winner_username)}
          </p>
        )}

        <div className="mt-3 space-y-1.5 border-t border-ground-edge pt-3">
          <Prize contest={contest} />
          {contest.has_sample_pack && (
            <div className="flex items-center justify-between">
              <span className="text-[11px] uppercase tracking-[0.14em] text-chrome-dim">{copy.contests.samplePack}</span>
              <Icon name="album" className="h-4 w-4 text-gold" />
            </div>
          )}
        </div>

        {contest.state === "open" && contest.preview_url && (
          <div className="mt-3">
            <Preview contest={contest} player={player} />
          </div>
        )}

        <div className="mt-auto flex items-center justify-between gap-2 pt-4">
          <span
            className={`whitespace-nowrap text-[10px] font-bold uppercase tracking-[0.14em] ${
              contest.admission.kind === "free" ? "text-money" : "text-chrome"
            }`}
          >
            {admissionText(contest)}
          </span>
          {contest.state === "open" && contest.seats_total > 0 && (
            <span className="bf-tabular whitespace-nowrap text-[10px] uppercase tracking-[0.12em] text-chrome-dim">
              {seatsOpen > 0 ? copy.contests.seatsLeft(seatsOpen) : copy.contests.full}
            </span>
          )}
        </div>
      </div>
    </article>
  );
}
